import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ThreeBackground from '../../components/ThreeBackground';
import { useAuth } from '../../contexts/AuthContext';
import { postService } from '../../lib/postService';
import Logo from '../../components/Logo';

const CurationPage = () => {
  const { user: authUser, signOut, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // ---------- user-derived state ----------
  const currentUser = authUser
    ? {
        id: authUser.id,
        name: authUser.user_metadata?.full_name || authUser.email,
        username: authUser.user_metadata?.username || authUser.email?.split('@')[0],
        avatar:
          authUser.user_metadata?.avatar_url ||
          'https://images.unsplash.com/photo-1611695434398-4f4b330623e6?w=400&h=400&fit=crop',
      }
    : null;

  // ---------- posts / media ----------
  const [posts, setPosts] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPosts = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const data = await postService.getUserPosts(currentUser.id);
      setPosts(data || []);
      const media = (data || []).reduce((acc, post) => {
        if (post.image_url) acc.push({ type: 'image', url: post.image_url, postId: post.id });
        if (post.video_url) acc.push({ type: 'video', url: post.video_url, postId: post.id });
        if (post.images) {
          post.images.forEach((img) => {
            if (img.url) acc.push({ type: 'image', url: img.url, postId: post.id });
          });
        }
        return acc;
      }, []);
      setMediaItems(media);
    } catch (err) {
      console.error('Error loading user posts:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // ---------- editable name ----------
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');

  const startEditingName = () => {
    setNameInput(currentUser?.name || '');
    setEditingName(true);
  };

  const saveName = () => {
    const trimmed = nameInput.trim();
    if (trimmed && trimmed !== currentUser?.name) {
      updateUser({ fullName: trimmed });
    }
    setEditingName(false);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') saveName();
    if (e.key === 'Escape') setEditingName(false);
  };

  // ---------- changeable avatar ----------
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handlePublicUrl = () => {
    const publicUrl = window.prompt("Enter a public image URL for your profile photo:");
    if (publicUrl) {
      try {
        updateUser({ avatarUrl: publicUrl });
      } catch (err) {
        console.error("Failed to update user:", err);
      }
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingAvatar(true);
    try {
      // Create a temporary object URL to load the image
      const tempUrl = URL.createObjectURL(file);
      
      // Compress the image before saving to base64
      const compressedBase64 = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Max dimensions for avatar
          const MAX_WIDTH = 400;
          const MAX_HEIGHT = 400;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          // Export as JPEG with 0.8 quality to save space
          resolve(canvas.toDataURL('image/jpeg', 0.8));
          URL.revokeObjectURL(tempUrl);
        };
        img.onerror = () => {
          reject(new Error("Failed to load image for compression"));
          URL.revokeObjectURL(tempUrl);
        };
        img.src = tempUrl;
      });

      try {
        updateUser({ avatarUrl: compressedBase64 });
      } catch (err) {
        console.error("Storage error:", err);
        alert("Image is still too large after compression. Try a different image or use a public URL.");
      }
    } catch (err) {
      console.error('Avatar upload failed:', err);
      alert("Failed to process photo. Please try a different image.");
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ---------- tabs ----------
  const [activeTab, setActiveTab] = useState('media');

  // ---------- video lightbox ----------
  const [activeVideo, setActiveVideo] = useState(null);

  // ---------- privacy toggles (Management tab) ----------
  const [privacyToggles, setPrivacyToggles] = useState({
    profileVisibility: true,
    showActivity: true,
    allowMessages: false,
  });

  const togglePrivacy = (key) => {
    setPrivacyToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ---------- derived stats ----------
  const momentsCount = posts.length;
  const appreciationsCount = posts.reduce((sum, p) => {
    const likes = Array.isArray(p.likes) ? p.likes.length : typeof p.likes === 'number' ? p.likes : 0;
    return sum + likes;
  }, 0);
  const collectionsCount = mediaItems.length;

  // ---------- logout ----------
  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  // ================================================================
  //  RENDER
  // ================================================================
  return (
    <>
      <ThreeBackground />

      {/* ======================== TOP NAV ======================== */}
      <header className="fixed top-0 w-full z-50 bg-background/40 backdrop-blur-[40px] border-b border-primary/10 h-20">
        <div className="flex justify-between items-center w-full px-margin-mobile md:px-margin-desktop max-w-container-max mx-auto h-full">
          <div
            className="cursor-pointer"
            onClick={() => navigate('/')}
          >
            <Logo />
          </div>
          <nav className="hidden md:flex items-center space-x-12">
            <span
              className="font-label-caps text-label-caps text-primary cursor-pointer active-nav-indicator"
            >
              Curation
            </span>
            <span
              className="font-label-caps text-label-caps text-on-surface-variant hover:text-primary transition-colors duration-300 cursor-pointer"
              onClick={() => navigate('/')}
            >
              Feed
            </span>
            <span 
              className="font-label-caps text-label-caps text-on-surface-variant hover:text-primary transition-colors duration-300 cursor-pointer"
              onClick={() => navigate('/salon')}
            >
              Salon
            </span>
          </nav>
          <div className="flex items-center space-x-6">
            <span className="material-symbols-outlined text-primary cursor-pointer hover:opacity-80 transition-opacity">
              notifications
            </span>
            <span
              className="material-symbols-outlined text-primary cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleLogout}
            >
              logout
            </span>
          </div>
        </div>
      </header>

      {/* ======================== SIDEBAR (Desktop) ======================== */}
      <aside className="fixed left-0 top-0 h-screen w-64 z-40 bg-surface-container-low/30 backdrop-blur-[24px] border-r border-white/5 flex-col p-stack-sm pt-24 hidden md:flex">
        {currentUser && (
          <div className="flex items-center space-x-4 mb-10 px-4">
            <div className="w-10 h-10 rounded-full bg-surface-variant overflow-hidden rose-gold-frame p-[1px] relative">
              <img
                alt="Curator"
                className="w-full h-full object-cover rounded-full"
                src={currentUser.avatar}
              />
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-background" />
            </div>
            <div>
              <div className="font-body-md text-primary font-bold">{currentUser.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-on-surface-variant/60">
                Elite Member
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 flex flex-col space-y-2">
          <span
            onClick={() => navigate('/')}
            className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300 cursor-pointer"
          >
            <span className="material-symbols-outlined">auto_awesome</span>
            <span className="font-body-md">Feed</span>
          </span>
          <span
            className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300 cursor-pointer"
          >
            <span className="material-symbols-outlined">explore</span>
            <span className="font-body-md">Discovery</span>
          </span>
          <span
            className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300 cursor-pointer"
          >
            <span className="material-symbols-outlined">task_alt</span>
            <span className="font-body-md">Moments</span>
          </span>
          <span
            className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300 cursor-pointer"
            onClick={() => navigate('/architecture')}
          >
            <span className="material-symbols-outlined">domain</span>
            <span className="font-body-md">Architecture</span>
          </span>
          <span
            className="flex items-center space-x-4 px-4 py-3 text-on-surface-variant hover:bg-white/10 rounded-full transition-all duration-300 cursor-pointer"
          >
            <span className="material-symbols-outlined">chat_bubble_outline</span>
            <span className="font-body-md">Messages</span>
          </span>
          <span
            className="flex items-center space-x-4 px-4 py-3 text-primary font-bold bg-white/5 rounded-full transition-all duration-300 cursor-pointer"
          >
            <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
              account_circle
            </span>
            <span className="font-body-md">Curation</span>
          </span>
        </nav>

        <button
          className="mt-8 mb-8 mx-4 py-3 border border-white/20 rounded-full font-label-caps text-label-caps hover:bg-white/5 transition-colors uppercase tracking-[0.2em] text-white"
          onClick={() => navigate('/')}
        >
          Post Entry
        </button>

        <div className="flex flex-col space-y-2 pt-8 border-t border-white/5">
          <span className="flex items-center space-x-4 px-4 py-2 text-on-surface-variant hover:text-primary transition-colors cursor-pointer">
            <span className="material-symbols-outlined text-[16px]">settings</span>
            <span className="text-xs font-label-caps uppercase text-white/60">Settings</span>
          </span>
          <span className="flex items-center space-x-4 px-4 py-2 text-on-surface-variant hover:text-primary transition-colors cursor-pointer">
            <span className="material-symbols-outlined text-[16px]">help_outline</span>
            <span className="text-xs font-label-caps uppercase text-white/60">Support</span>
          </span>
        </div>
      </aside>

      {/* ======================== MAIN CONTENT ======================== */}
      <main className="md:ml-64 pt-28 px-margin-mobile md:px-margin-desktop pb-32 max-w-container-max relative z-10 min-h-screen">
        <div className="max-w-4xl mx-auto space-y-stack-lg">

          {/* ============ PROFILE HEADER CARD ============ */}
          <section className="glass-panel rounded-xl p-8 md:p-12 animate-fade-in">
            {/* Cover gradient accent */}
            <div className="absolute inset-x-0 top-0 h-32 rounded-t-xl overflow-hidden pointer-events-none" style={{
              background: 'linear-gradient(135deg, rgba(183,110,96,0.25) 0%, rgba(183,110,96,0.05) 50%, transparent 100%)'
            }} />

            <div className="relative flex flex-col md:flex-row items-center md:items-start gap-8">
              {/* --- Avatar --- */}
              <div className="relative group shrink-0">
                <div
                  className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden cursor-pointer"
                  style={{
                    border: '3px solid rgba(183, 110, 96, 0.6)',
                    boxShadow: '0 0 30px rgba(183, 110, 96, 0.25), inset 0 0 15px rgba(183, 110, 96, 0.1)',
                  }}
                  onClick={handleAvatarClick}
                >
                  {uploadingAvatar ? (
                    <div className="w-full h-full flex items-center justify-center bg-surface-container-high">
                      <span className="material-symbols-outlined text-primary animate-spin text-3xl">progress_activity</span>
                    </div>
                  ) : (
                    <img
                      alt="Profile"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      src={currentUser?.avatar}
                    />
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center rounded-full">
                    <span className="material-symbols-outlined text-white text-2xl">photo_camera</span>
                  </div>
                </div>

                {/* Verified badge */}
                <div
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #b76e60, #d4956a)',
                    boxShadow: '0 0 12px rgba(183, 110, 96, 0.4)',
                  }}
                >
                  <span className="material-symbols-outlined text-white text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                    verified
                  </span>
                </div>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleAvatarChange}
                />
              </div>

              {/* --- Profile Info --- */}
              <div className="flex-1 text-center md:text-left space-y-4">
                {/* Editable Name */}
                {editingName ? (
                  <div className="flex items-center gap-3 justify-center md:justify-start">
                    <input
                      autoFocus
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      onKeyDown={handleNameKeyDown}
                      onBlur={saveName}
                      className="bg-transparent border-b-2 border-primary/40 focus:border-primary text-white font-headline-lg text-3xl md:text-4xl outline-none py-1 px-1 w-auto min-w-[200px] transition-colors"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    />
                    <button
                      onClick={saveName}
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    >
                      <span className="material-symbols-outlined text-primary text-[18px]">check</span>
                    </button>
                  </div>
                ) : (
                  <h1
                    className="font-headline-xl text-3xl md:text-4xl text-white cursor-pointer group/name inline-flex items-center gap-3"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                    onClick={startEditingName}
                  >
                    <span>{currentUser?.name || 'Curator'}</span>
                    <span className="material-symbols-outlined text-on-surface-variant/40 group-hover/name:text-primary text-[20px] transition-colors">
                      edit
                    </span>
                  </h1>
                )}

                <p className="font-label-caps text-label-caps text-on-surface-variant/60 tracking-[0.2em]">
                  @{currentUser?.username || 'curator'}
                </p>

                {/* Stats Row */}
                <div className="flex items-center gap-8 justify-center md:justify-start pt-2">
                  <div className="text-center">
                    <div className="font-headline-lg text-2xl text-white stat-glow">{momentsCount}</div>
                    <div className="font-label-caps text-[10px] tracking-[0.15em] text-on-surface-variant/60 uppercase mt-1">
                      Moments
                    </div>
                  </div>
                  <div className="w-px h-8 bg-white/10" />
                  <div className="text-center">
                    <div className="font-headline-lg text-2xl text-white stat-glow">{appreciationsCount}</div>
                    <div className="font-label-caps text-[10px] tracking-[0.15em] text-on-surface-variant/60 uppercase mt-1">
                      Appreciations
                    </div>
                  </div>
                  <div className="w-px h-8 bg-white/10" />
                  <div className="text-center">
                    <div className="font-headline-lg text-2xl text-white stat-glow">{collectionsCount}</div>
                    <div className="font-label-caps text-[10px] tracking-[0.15em] text-on-surface-variant/60 uppercase mt-1">
                      Collections
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-4 justify-center md:justify-start pt-3">
                  <button
                    className="px-6 py-2.5 rounded-full font-label-caps text-label-caps tracking-[0.15em] uppercase transition-all duration-300 hover:shadow-lg"
                    style={{
                      background: 'linear-gradient(135deg, rgba(183,110,96,0.3), rgba(183,110,96,0.15))',
                      border: '1px solid rgba(183, 110, 96, 0.4)',
                      color: '#e8c4b8',
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-[16px]">palette</span>
                      Edit Aesthetic
                    </span>
                  </button>
                  <button
                    className="px-6 py-2.5 rounded-full font-label-caps text-label-caps tracking-[0.15em] uppercase border border-white/20 text-white hover:bg-white/5 transition-all duration-300"
                    onClick={() => navigate('/')}
                  >
                    <span className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-[16px]">add_circle</span>
                      Post Entry
                    </span>
                  </button>
                  <button
                    className="px-6 py-2.5 rounded-full font-label-caps text-label-caps tracking-[0.15em] uppercase border border-white/20 text-white hover:bg-white/5 transition-all duration-300"
                    onClick={handlePublicUrl}
                  >
                    <span className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-[16px]">link</span>
                      Link Photo
                    </span>
                  </button>
                  {authUser?.email?.toLowerCase().includes('admin') && (
                    <button
                      className="px-6 py-2.5 rounded-full font-label-caps text-label-caps tracking-[0.15em] uppercase border border-error text-error hover:bg-error/10 transition-all duration-300"
                      onClick={() => navigate('/admin')}
                    >
                      <span className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[16px]">admin_panel_settings</span>
                        Admin Panel
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* ============ TABS ============ */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {[
              { key: 'media', label: 'Media Archive', icon: 'photo_library' },
              { key: 'collections', label: 'Collections', icon: 'collections_bookmark' },
              { key: 'management', label: 'Management', icon: 'tune' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-3 rounded-full font-label-caps text-label-caps tracking-[0.1em] uppercase transition-all duration-300 whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-white/10 text-primary border border-white/15'
                    : 'text-on-surface-variant/60 hover:text-white hover:bg-white/5 border border-transparent'
                }`}
              >
                <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ============ TAB CONTENT ============ */}

          {/* --- MEDIA ARCHIVE TAB --- */}
          {activeTab === 'media' && (
            <section className="animate-fade-in">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-headline-lg text-headline-lg text-white italic">
                  Media Archive
                </h2>
                <span className="font-label-caps text-label-caps text-on-surface-variant/60 tracking-[0.15em]">
                  {mediaItems.length} {mediaItems.length === 1 ? 'ITEM' : 'ITEMS'}
                </span>
              </div>

              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="skeleton aspect-square rounded-xl"
                      style={{ animationDelay: `${i * 100}ms` }}
                    />
                  ))}
                </div>
              ) : mediaItems.length === 0 ? (
                /* Empty State */
                <div className="glass-panel rounded-xl p-16 text-center space-y-6">
                  <div
                    className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(183,110,96,0.15), rgba(183,110,96,0.05))',
                      border: '1px solid rgba(183, 110, 96, 0.2)',
                    }}
                  >
                    <span className="material-symbols-outlined text-4xl" style={{ color: '#b76e60' }}>
                      photo_library
                    </span>
                  </div>
                  <div>
                    <h3 className="font-headline-lg text-xl text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      No media yet
                    </h3>
                    <p className="font-body-md text-on-surface-variant/60 max-w-sm mx-auto">
                      Your curated media archive will appear here once you share photos and videos in your posts.
                    </p>
                  </div>
                  <button
                    className="px-6 py-3 rounded-full font-label-caps text-label-caps tracking-[0.15em] uppercase transition-all duration-300"
                    style={{
                      background: 'linear-gradient(135deg, rgba(183,110,96,0.3), rgba(183,110,96,0.15))',
                      border: '1px solid rgba(183, 110, 96, 0.4)',
                      color: '#e8c4b8',
                    }}
                    onClick={() => navigate('/')}
                  >
                    Create Your First Post
                  </button>
                </div>
              ) : (
                /* Bento / Masonry Grid */
                <div className="columns-2 md:columns-3 gap-4 space-y-4">
                  {mediaItems.map((item, idx) => (
                    <div
                      key={`${item.postId}-${idx}`}
                      className="break-inside-avoid group relative rounded-xl overflow-hidden cursor-pointer glass-panel border-0"
                      style={{ animationDelay: `${idx * 60}ms` }}
                      onClick={() => {
                        if (item.type === 'video') setActiveVideo(item.url);
                      }}
                    >
                      {item.type === 'image' ? (
                        <img
                          src={item.url}
                          alt={`Media ${idx + 1}`}
                          className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />
                      ) : (
                        <div className="relative">
                          <video
                            src={item.url}
                            className="w-full h-auto object-cover"
                            muted
                            preload="metadata"
                          />
                          {/* Play icon overlay */}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors duration-300">
                            <div
                              className="w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md transition-transform duration-300 group-hover:scale-110"
                              style={{
                                background: 'rgba(183, 110, 96, 0.6)',
                                border: '1px solid rgba(255,255,255,0.2)',
                              }}
                            >
                              <span className="material-symbols-outlined text-white text-2xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                                play_arrow
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Hover gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                      {/* Hover info */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <div className="flex items-center gap-2 text-white/80">
                          <span className="material-symbols-outlined text-[16px]">
                            {item.type === 'video' ? 'videocam' : 'image'}
                          </span>
                          <span className="font-label-caps text-[10px] tracking-[0.15em] uppercase">
                            {item.type === 'video' ? 'Video' : 'Photo'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* --- COLLECTIONS TAB --- */}
          {activeTab === 'collections' && (
            <section className="animate-fade-in">
              <h2 className="font-headline-lg text-headline-lg text-white italic mb-6">
                Collections
              </h2>
              <div className="glass-panel rounded-xl p-16 text-center space-y-6">
                <div
                  className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(183,110,96,0.15), rgba(183,110,96,0.05))',
                    border: '1px solid rgba(183, 110, 96, 0.2)',
                  }}
                >
                  <span className="material-symbols-outlined text-4xl" style={{ color: '#b76e60' }}>
                    collections_bookmark
                  </span>
                </div>
                <div>
                  <h3
                    className="font-headline-lg text-xl text-white mb-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    Curated Collections
                  </h3>
                  <p className="font-body-md text-on-surface-variant/60 max-w-md mx-auto">
                    Organize your moments into beautifully curated collections. This feature is coming soon to elevate your AETHER experience.
                  </p>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                  <span className="material-symbols-outlined text-[14px]" style={{ color: '#b76e60' }}>schedule</span>
                  <span className="font-label-caps text-[10px] tracking-[0.15em] uppercase text-on-surface-variant/60">
                    Coming Soon
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* --- MANAGEMENT TAB --- */}
          {activeTab === 'management' && (
            <section className="animate-fade-in space-y-6">
              <h2 className="font-headline-lg text-headline-lg text-white italic mb-2">
                Management
              </h2>

              {/* Profile Privacy */}
              <div className="glass-panel rounded-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-[20px]" style={{ color: '#b76e60' }}>
                    shield
                  </span>
                  <h3
                    className="font-headline-lg text-lg text-white"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    Profile Privacy
                  </h3>
                </div>

                {[
                  {
                    key: 'profileVisibility',
                    label: 'Public Profile',
                    description: 'Allow others to view your curation page',
                    icon: 'visibility',
                  },
                  {
                    key: 'showActivity',
                    label: 'Show Activity Status',
                    description: 'Display when you are online or last active',
                    icon: 'circle',
                  },
                  {
                    key: 'allowMessages',
                    label: 'Direct Messages',
                    description: 'Allow non-connections to send you messages',
                    icon: 'chat',
                  },
                ].map((item) => (
                  <div
                    key={item.key}
                    className="flex items-center justify-between py-4 border-b border-white/5 last:border-0"
                  >
                    <div className="flex items-center gap-4">
                      <span className="material-symbols-outlined text-on-surface-variant/60 text-[20px]">
                        {item.icon}
                      </span>
                      <div>
                        <div className="font-body-md text-white">{item.label}</div>
                        <div className="text-[12px] text-on-surface-variant/50 mt-0.5">
                          {item.description}
                        </div>
                      </div>
                    </div>
                    {/* Toggle */}
                    <button
                      onClick={() => togglePrivacy(item.key)}
                      className={`relative w-12 h-7 rounded-full transition-all duration-300 ${
                        privacyToggles[item.key]
                          ? 'bg-[#b76e60]/40 border border-[#b76e60]/60'
                          : 'bg-white/10 border border-white/15'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 rounded-full transition-all duration-300 ${
                          privacyToggles[item.key]
                            ? 'left-[calc(100%-24px)] bg-[#b76e60] shadow-[0_0_8px_rgba(183,110,96,0.5)]'
                            : 'left-1 bg-white/50'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>

              {/* Archive Analytics Card */}
              <div className="glass-panel rounded-xl p-8 space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-[20px]" style={{ color: '#b76e60' }}>
                    analytics
                  </span>
                  <h3
                    className="font-headline-lg text-lg text-white"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    Archive Analytics
                  </h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Profile Views', value: '2.4K', icon: 'visibility', trend: '+12%' },
                    { label: 'Engagements', value: appreciationsCount.toString(), icon: 'favorite', trend: '+8%' },
                    { label: 'Media Items', value: collectionsCount.toString(), icon: 'photo_library', trend: '—' },
                    { label: 'Reach', value: '1.2K', icon: 'trending_up', trend: '+24%' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="rounded-xl p-4 text-center"
                      style={{
                        background: 'rgba(255,255,255,0.03)',
                        border: '0.5px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <span className="material-symbols-outlined text-on-surface-variant/40 text-[20px] mb-2 block">
                        {stat.icon}
                      </span>
                      <div className="font-headline-lg text-xl text-white stat-glow">{stat.value}</div>
                      <div className="font-label-caps text-[9px] tracking-[0.15em] text-on-surface-variant/50 uppercase mt-1">
                        {stat.label}
                      </div>
                      <div
                        className="text-[10px] mt-2 font-bold"
                        style={{ color: stat.trend.startsWith('+') ? '#10b981' : '#a1a1aa' }}
                      >
                        {stat.trend}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Engagement chart placeholder */}
                <div
                  className="rounded-xl p-6"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '0.5px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-label-caps text-[11px] tracking-[0.15em] text-on-surface-variant/60 uppercase">
                      Engagement Trend — Last 30 Days
                    </span>
                    <span className="material-symbols-outlined text-on-surface-variant/30 text-[16px]">
                      info
                    </span>
                  </div>
                  <div className="flex items-end gap-1 h-24">
                    {[35, 50, 40, 65, 55, 70, 60, 80, 75, 85, 70, 90, 85, 95, 88].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t transition-all duration-500"
                        style={{
                          height: `${h}%`,
                          background: `linear-gradient(to top, rgba(183,110,96,${0.3 + (h / 200)}), rgba(183,110,96,0.05))`,
                          animationDelay: `${i * 50}ms`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

      {/* ======================== VIDEO LIGHTBOX ======================== */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md animate-fade-in"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative max-w-4xl w-full mx-4 rounded-xl overflow-hidden glass-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center hover:bg-black/70 transition-colors"
              onClick={() => setActiveVideo(null)}
            >
              <span className="material-symbols-outlined text-white">close</span>
            </button>
            <video
              src={activeVideo}
              className="w-full"
              controls
              autoPlay
              playsInline
            />
          </div>
        </div>
      )}

      {/* ======================== MOBILE BOTTOM NAV ======================== */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/60 backdrop-blur-[40px] border-t border-white/5">
        <div className="flex items-center justify-around py-3">
          <button className="flex flex-col items-center gap-1 text-on-surface-variant/60" onClick={() => navigate('/')}>
            <span className="material-symbols-outlined text-[22px]">auto_awesome</span>
            <span className="text-[9px] font-label-caps uppercase tracking-widest">Feed</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-on-surface-variant/60">
            <span className="material-symbols-outlined text-[22px]">explore</span>
            <span className="text-[9px] font-label-caps uppercase tracking-widest">Explore</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-on-surface-variant/60">
            <span className="material-symbols-outlined text-[22px]">chat_bubble_outline</span>
            <span className="text-[9px] font-label-caps uppercase tracking-widest">Messages</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-primary">
            <span
              className="material-symbols-outlined text-[22px]"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              account_circle
            </span>
            <span className="text-[9px] font-label-caps uppercase tracking-widest">Curation</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default CurationPage;
